import type { BLEND_MODES, Point } from 'pixi.js';
import type { CommonModel, ModelType } from './models';

export type GraphicsModel = CommonModel<ModelType.Graphics> & {
  type: ModelType.Graphics;
  pivot: Point;
  tint: number;
  blendMode: BLEND_MODES;
  commands: GraphicsCommand[];
};

export enum GraphicsCommandType {
  BeginFill = 'beginFill',
  LineStyle = 'lineStyle',
  DrawRect = 'drawRect',
  DrawRoundedRect = 'drawRoundedRect',
  DrawCircle = 'drawCircle',
  DrawEllipse = 'drawEllipse',
  DrawStar = 'drawStar',
  DrawPolygon = 'drawPolygon',
  MoveTo = 'moveTo',
  LineTo = 'lineTo',
  Arc = 'arc',
  ArcTo = 'arcTo',
  QuadraticCurveTo = 'quadraticCurveTo',
  BezierCurveTo = 'bezierCurveTo',
  BeginHole = 'beginHole',
  EndHole = 'endHole',
  ClosePath = 'closePath',
  EndFill = 'endFill',
}

type GraphicsBeginFillCommand = {
  type: GraphicsCommandType.BeginFill;
  color: number;
  alpha: number;
};

type GraphicsLineStyleCommand = {
  type: GraphicsCommandType.LineStyle;
  width: number;
  color: number;
  alpha: number;
  alignment: number;
};

type GraphicsDrawRectCommand = {
  type: GraphicsCommandType.DrawRect;
  x: number;
  y: number;
  width: number;
  height: number;
};

type GraphicsDrawRoundedRectCommand = {
  type: GraphicsCommandType.DrawRoundedRect;
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
};

type GraphicsDrawCircleCommand = {
  type: GraphicsCommandType.DrawCircle;
  x: number;
  y: number;
  radius: number;
};

type GraphicsDrawEllipseCommand = {
  type: GraphicsCommandType.DrawEllipse;
  x: number;
  y: number;
  width: number;
  height: number;
};

type GraphicsDrawStarCommand = {
  type: GraphicsCommandType.DrawStar;
  x: number;
  y: number;
  points: number;
  radius: number;
  innerRadius: number;
  rotation: number;
};

type GraphicsDrawPolygonCommand = {
  type: GraphicsCommandType.DrawPolygon;
  path: string;
};

type GraphicsMoveToCommand = {
  type: GraphicsCommandType.MoveTo;
  x: number;
  y: number;
};

type GraphicsLineToCommand = {
  type: GraphicsCommandType.LineTo;
  x: number;
  y: number;
};

type GraphicsArcCommand = {
  type: GraphicsCommandType.Arc;
  cx: number;
  cy: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  anticlockwise: boolean;
};

type GraphicsArcToCommand = {
  type: GraphicsCommandType.ArcTo;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  radius: number;
};

type GraphicsQuadraticCurveToCommand = {
  type: GraphicsCommandType.QuadraticCurveTo;
  cpX: number;
  cpY: number;
  toX: number;
  toY: number;
};

type GraphicsBezierCurveToCommand = {
  type: GraphicsCommandType.BezierCurveTo;
  cpX: number;
  cpY: number;
  cpX2: number;
  cpY2: number;
  toX: number;
  toY: number;
};

type GraphicsBeginHoleCommand = {
  type: GraphicsCommandType.BeginHole;
};

type GraphicsEndHoleCommand = {
  type: GraphicsCommandType.EndHole;
};

type GraphicsClosePathCommand = {
  type: GraphicsCommandType.ClosePath;
};

type GraphicsEndFillCommand = {
  type: GraphicsCommandType.EndFill;
};

type GraphicsCommand =
  | GraphicsBeginFillCommand
  | GraphicsLineStyleCommand
  | GraphicsDrawRectCommand
  | GraphicsDrawRoundedRectCommand
  | GraphicsDrawCircleCommand
  | GraphicsDrawEllipseCommand
  | GraphicsDrawStarCommand
  | GraphicsDrawPolygonCommand
  | GraphicsMoveToCommand
  | GraphicsLineToCommand
  | GraphicsArcCommand
  | GraphicsArcToCommand
  | GraphicsQuadraticCurveToCommand
  | GraphicsBezierCurveToCommand
  | GraphicsBeginHoleCommand
  | GraphicsEndHoleCommand
  | GraphicsClosePathCommand
  | GraphicsEndFillCommand;
